import { Popover, PopoverContent, PopoverTrigger, Portal } from '@chakra-ui/react';
import { FreezeColumn, FreezeTable, TableIcon } from '@sketchnote/icons';
import { Divider } from '@sketchnote/shared-components';
import {
  PrioritySelectOption,
  PrioritySelectSingleValue,
  prioritySelectStyles,
  UserSelectDropDownIndicator,
} from '@sketchnote/shared-components';
import cn from 'classnames';
import * as React from 'react';
import Select from 'react-select';

type FreezeValue = 0 | 1 | 2;

export interface FreezePaneProps {
  isOpen: boolean;
  setIsOpen: (value: boolean) => void;
  freezeValue?: FreezeValue;
  onChange: (value: FreezeValue) => void;
}

export const FreezePane = ({ isOpen, setIsOpen, freezeValue, onChange }: FreezePaneProps) => {
  const toggleMenu = (value?: boolean) => () => {
    setIsOpen(value ?? !isOpen);
  };

  const handleOnChange = (option) => {
    onChange(freezeValueToIndex[option.value]);
  };

  return (
    <Popover
      placement="bottom"
      isOpen={isOpen}
      onOpen={toggleMenu(true)}
      onClose={toggleMenu(false)}
      modifiers={[{ name: 'eventListeners', options: { scroll: isOpen } }]}
    >
      <PopoverTrigger>
        <button
          className={cn('flex items-center text-sm', {
            'text-gray-999': isOpen,
            'text-gray-500': !isOpen,
          })}
        >
          <FreezeTable /> <span className="ml-6">Freeze</span>
        </button>
      </PopoverTrigger>
      <Portal>
        <PopoverContent>
          <div className="flex flex-col p-8 rounded-lg bg-white shadow-mdl3 text-gray-999 min-w-314">
            <p className="text-gray-500 text-sm p-8">
              Freeze pane for <TableIcon /> <span className="font-medium">Table</span>
            </p>
            <p className="text-gray-400 text-xs p-8">Freeze columns in place</p>
            <Divider style={{ marginBottom: '0.8rem', marginTop: '0.5rem' }} />
            <div className="m-8">
              <label className="flex items-center text-gray-600">
                <FreezeColumn className="mr-7" /> Freeze column{' '}
              </label>
              <Select
                options={[
                  {
                    label: 'Select columns to freeze',
                    options: freezeOptions,
                  },
                ]}
                onChange={handleOnChange}
                value={
                  freezeValue !== undefined && {
                    label: freezeLabelForNum[freezeValue] || '',
                    value: freezeValue,
                  }
                }
                styles={prioritySelectStyles}
                menuPlacement="bottom"
                placeholder="Select no of columns"
                className="mt-14"
                components={{
                  DropdownIndicator: UserSelectDropDownIndicator,
                  IndicatorSeparator: () => null,
                  SingleValue: PrioritySelectSingleValue,
                  Option: PrioritySelectOption,
                }}
              />
            </div>
          </div>
        </PopoverContent>
      </Portal>
    </Popover>
  );
};

const freezeOptions = [
  { label: 'First column', value: 'first' },
  { label: 'First two columns', value: 'first-two' },
  { label: 'Unfreeze column', value: 'unfreeze' },
];

const freezeLabelForNum = {
  0: freezeOptions[2].label,
  1: freezeOptions[0].label,
  2: freezeOptions[1].label,
};

const freezeValueToIndex: { [key: string]: FreezeValue } = {
  [freezeOptions[2].value]: 0,
  [freezeOptions[0].value]: 1,
  [freezeOptions[1].value]: 2,
};
